<template>
  <a
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-media': showSecondaryMedia,
      'has-details-animation': hasDetailsAnimation,
      'small-card': smallCard,
      'product-card__inline': inline
    }"
    :href="quickview ? '#' : product.url"
    @click="handleClick(product.handle, product.url)"
    @focus="loadSecondaryMedia = true"
    @focusout="loadSecondaryMedia = false"
    @mouseenter="loadSecondaryMedia = true"
    @mouseleave="loadSecondaryMedia = false"
  >
    <div class="product-card__image-container">




      <responsive-image
        class="product-card__primary-image"
      
        :image="product.plpImage?.src ? product.plpImage : product.featuredImage"
   
        :lazyload="lazyload"
        :ratio="ratio"
        sizes="(min-width: 1024px) 25vw, 50vw"
      />

      <responsive-image
        v-if="product.secondaryMedia?.mediaType === 'image'"
        class="product-card__secondary-media"
        :image="product.secondaryMedia?.image
          ? product.secondaryMedia.image
          : product.secondaryMedia"
        :padding="false"
        :ratio="ratio"
        sizes="(min-width: 1024px) 25vw, 50vw"
        @image-loaded="showSecondaryMedia = true"
      />

      <video-player
        v-if="product.secondaryMedia?.mediaType === 'video'"
        class="product-card__secondary-media"
        :image="product.secondaryMedia.previewImage"
        :intersection-observer="{
          enable: false,
        }"
        :controls="{
          playPause: false,
        }"
        :is-active="loadSecondaryMedia"
        :ratio="ratio"
        sizes="(min-width: 1024px) 25vw, 50vw"
        :sources="product.secondaryMedia.sources"
        @video-state-update="state => state === 'ready' ? showSecondaryMedia = true : null"
      />

      <product-badges
        v-if="showBadges && product?.badges?.length > 0"
        class="product-card__badges"
        :badges="product?.badges"
      />
    </div>

    <div class="product-card__footer">
      <p
        v-if="product?.shortDescription"
        class="
          product-card__short-description
          text-body-s-regular
        "
        v-text="product?.shortDescription"
      />

      <p
        class="product-card__title text-body-l-bold"
        v-text="product?.title"
      />

      <product-prices
        class="product-card__prices"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :loading="!product"
        :price="product?.price?.amount"
        :varies="product?.price?.varies"
        :price-first="false"
      />

      <p
        v-if="product?.availableOptions"
        class="text-body-xs-regular product-card__available-options mobile"
        v-text="`${$string('product.available_in')} ${product?.availableOptions}`"
      />

      <div
        v-if="!hideAdditionalData"
        class="product-card__additional-data"
      >
        <p
          v-if="product?.availableOptions"
          class="text-body-xs-regular product-card__available-options desktop"
          v-text="`${$string('product.available_in')} ${product?.availableOptions}`"
        />

        <span
          v-if="showTasteNotes && product.isCaviarProduct"
          class="product-card__divider"
        />

        <taste-notes
          v-if="showTasteNotes && product?.isCaviarProduct"
          class="product-card__taste-notes"
          :notes="filteredTasteNotes"
        />

        <span
          v-if="showDescription"
          class="product-card__divider"
        />

        <p
          v-if="showDescription"
          class="text-body-xs-regular text-left"
          v-text="product?.additionalDescription"
        />
      </div>
    </div>
  </a>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean} lazyload - Lazyload image, defaults to true.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [showBadges] - Shows badges.
 * @param {Boolean} [showTasteNotes] - Shows taste notes.
 * @param {Array} [tasteNotesKey] - Key list to show.
 * @param {Boolean} [showCaviarDescription] - Shows description for caviar.
 * @param {Boolean} [hasDetailsAnimation] - Shows additional data on hover.
 * @param {Boolean} [smallCard] - Shows additional data.
 * @param {Boolean} [inline] - Applies styling for grid version.
 * @param {String} [ratio] - Media ratio.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions } from 'vuex'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import ProductBadges from '~async/product-badges/product-badges'
import TasteNotes from '~async/taste-notes/taste-notes'

export default {
  name: 'ProductCard',

  components: {
    ProductPrices,
    ResponsiveImage,
    ProductBadges,
    TasteNotes,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~global/video-player/video-player'
      ),
    }),
  },

  props: {
    lazyload: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    showTasteNotes: {
      type: Boolean,
      default: true,
    },
    tasteNotesKey: {
      type: Array,
      default: () => ['size', 'texture', 'intensity'],
    },
    showCaviarDescription: {
      type: Boolean,
      default: false,
    },
    smallCard: {
      type: Boolean,
      default: false,
    },
    hasDetailsAnimation: {
      type: Boolean,
      default: false,
    },
    hideAdditionalData: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    ratio: {
      type: String,
      default: '4:5',
    },
    quickview: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadSecondaryMedia: false,
      showSecondaryMedia: false,
    }
  },

  computed: {

    /**
     * Returns true if has description.
     * @returns {Boolean}
     */
    showDescription() {
      return (!this.product?.isCaviarProduct || this.showCaviarDescription) &&
        this.product?.additionalDescription
    },

    /**
     * Returns true if has description.
     * @returns {Array}
     */
    filteredTasteNotes() {
      return this.product?.tasteNotes.filter(notes => this.tasteNotesKey.includes(notes.key))
    },
  },

  methods: {
    ...mapActions({
      setQuickViewHandle: 'quick-view/setHandle',
    }),

    handleClick(handle, url) {
      if (this.quickview) {
        this.setQuickViewHandle(handle)
      } else {
        window.location.href = url
      }
    },
  },
}
</script>
